/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { Close } from '@carbon/react/icons';
import { useTranslation } from '../../../../../Utilities/i18n/i18n.js';

/**
 * `ListBoxSelection` is used to provide controls for clearing a selection, in
 * addition to conditionally rendering a badge if the control has more than one
 * selection.
 */
var ListBoxSelection = function ListBoxSelection(_ref) {
  var clearSelection = _ref.clearSelection,
    selectionCount = _ref.selectionCount,
    overrideTranslations = _ref.overrideTranslations;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var className = classnames({
    'cds--list-box__selection': true,
    'cds--list-box__selection--multi': selectionCount
  });
  var handleOnClick = function handleOnClick(event) {
    // If we have a mult-select badge, clicking it shouldn't open the menu back
    // up. However, if we have a clear badge then we want the click to have this
    // behavior.
    if (selectionCount) {
      event.stopPropagation();
    }
    clearSelection(event);
  };
  var handleOnKeyDown = function handleOnKeyDown(event) {
    // When a user hits ENTER, we'll clear the selection
    if (event.keyCode === 13) {
      clearSelection(event);
    }
  };
  var description = selectionCount ? (overrideTranslations === null || overrideTranslations === void 0 ? void 0 : overrideTranslations('clear.all')) || t('Clear all selected items') : (overrideTranslations === null || overrideTranslations === void 0 ? void 0 : overrideTranslations('clear.selection')) || t('Clear selected item');
  return /*#__PURE__*/React.createElement("div", {
    role: "button",
    className: className,
    tabIndex: "0",
    onClick: handleOnClick,
    onKeyDown: handleOnKeyDown,
    title: description
  }, selectionCount, /*#__PURE__*/React.createElement(Close, {
    focusable: false
  }));
};
ListBoxSelection.propTypes = {
  /**
   * Specify a function to be invoked when a user interacts with the clear
   * selection element.
   */
  clearSelection: PropTypes.func.isRequired,
  /**
   * Specify an optional `selectionCount` value that will be used to determine
   * whether the selection should display a badge or a single clear icon.
   */
  selectionCount: PropTypes.number,
  /**
   * i18n hook used to provide the appropriate description for the given menu
   * icon.
   */
  overrideTranslations: PropTypes.func
};
ListBoxSelection.defaultProps = {
  selectionCount: undefined,
  overrideTranslations: undefined
};
var ListBoxSelection$1 = ListBoxSelection;

export { ListBoxSelection$1 as default };
